import { appListLimits as limit, listLinks, sortOrders } from '@common/constants'
import { useAppDispatch, useAppSelector, useIframe, useTranslate } from '@common/hooks'
import { ErrorContext } from '@layouts/CommonLayout'
import { appListFetchThunks } from '@redux/slices/applicationList'
import type { IFilters } from '@redux/slices/applicationList/applicationList.types'
import { setPreviousListPage } from '@redux/slices/common'
import { fetchUserSlots } from '@redux/slices/userSlots/userSlots'
import Head from 'next/head'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useContext, useEffect, useMemo } from 'react'

import ApplicationList from './ApplicationList'
import classNames from './styles.module.scss'


interface IProps {
  clientSideMode?: boolean,
  title: '2024 Hackathon Winners' | 'Top earning apps' | 'Popular new apps' | 'Trending apps' | 'Search apps' | 'Recently updated apps' | 'Recently used apps',
  reducer: AppListReducer,
}

ApplicationCategory.defaultProps = {
  clientSideMode: false,
}

export default function ApplicationCategory({
  title,
  reducer,
}: IProps): JSX.Element {
  const dispatch = useAppDispatch()
  const { translate } = useTranslate()
  const router = useRouter()
  const slotId = useAppSelector(store => store.common.data.slotId)
  const applicationsReducer = useAppSelector(store => store[reducer].data)
  const token = useAppSelector(store => store.common.data.config?.token)
  const reducerError = useAppSelector(store => store[reducer].errors) as { message: string }
  const { setModalError } = useContext(ErrorContext)
  const filters = useAppSelector(store => store[reducer].filters)

  const titlePage = slotId
    ? translate(title) + ` | Slot ${slotId}`
    : translate(title)

  const iframeParams = useMemo(() => ({
    title: titlePage,
    url: `/apps/${listLinks[reducer]}`,
    params: { q: router.query.q || null, slot: slotId ? String(slotId) : null },
    onDashboardHasUpdate: () => {
      void dispatch(fetchUserSlots())
    },
  }), [titlePage, reducer, slotId, router.query.q])

  useIframe(iframeParams)

  useEffect(() => {
    dispatch(setPreviousListPage(null))
  }, [])

  useEffect(() => {
    if(reducer === 'recentlyUsed' && !token) {
      return
    }
    const page = router.query.page
    const offset = (typeof page === 'string' && page !== '' && Number(page) > 0) ? (Number(page) - 1) * limit : 0
    if(!applicationsReducer || (router.query.q !== filters.search || offset !== filters.offset)) {
      const params = {
        ...sortOrders[reducer],
        limit,
      } as IFilters
      if(router.query.q) {
        params.search = router.query.q as string
      } else {
        delete params.search
      }
      if(router.query.page) {
        params.offset = offset
      } else {
        params.offset = 0
      }
      void dispatch(appListFetchThunks[reducer](params))
    }
  }, [router.query, token])

  useEffect(() => {
    if(reducerError) {
      setModalError(reducerError)
    }
  }, [reducerError])

  return (
    <>
      <Head>
        <title>
          {titlePage}
        </title>
        <meta
          property="og:title"
          content={titlePage}
          key="title"
        />
      </Head>
      <main className={classNames.main}>
        <h2 className={classNames.title} data-test="app-sort-order">
          {translate(title)}
          <Link href="/apps" tabIndex={100} legacyBehavior>
            <a
              className={classNames.link}
              data-test="back-btn"
            >
              {translate('Back')}
            </a>
          </Link>
        </h2>
        <ApplicationList reducer={reducer}/>
      </main>
    </>
  )
}
